<template>
  <div class="cv-error-state">
    <v-icon :icon="content.icon" size="56" class="cv-error-icon mb-4" />
    <h2 class="text-h6 mb-2">{{ content.title }}</h2>
    <p class="cv-error-body mb-1">{{ content.body }}</p>

    <p v-if="traceId" class="cv-error-trace mb-0">
      Hiba azonosító: <code>{{ traceId }}</code>
    </p>

    <div class="cv-error-actions mt-6">
      <v-btn
        v-if="kind === 'unavailable'"
        color="primary"
        variant="flat"
        prepend-icon="mdi-refresh"
        @click="$emit('retry')"
      >
        Újrapróbálom
      </v-btn>
      <v-btn v-if="backTo" :to="backTo" variant="outlined">
        {{ backLabel }}
      </v-btn>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { ApiErrorKind } from "@/services/useApiError";

/**
 * One rendering for every way a resource can fail to load, so the seven detail views and the
 * catch-all routes all say the same thing. Takes the classified `kind` rather than a raw
 * status code: the views deal in categories, not HTTP.
 *
 * Rendered in place of the content rather than navigated to, so the URL survives — the user
 * can reload it, bookmark it, or send it to whoever gave them the link.
 */
const props = withDefaults(
  defineProps<{
    kind: ApiErrorKind;
    /** Cost-centre denial rather than a role denial. */
    costCenter?: boolean;
    costCenterName?: string;
    traceId?: string;
    backTo?: string;
    backLabel?: string;
  }>(),
  { backLabel: "Vissza" }
);

defineEmits<{ (e: "retry"): void }>();

const forbiddenBody = () => {
  const where = props.costCenterName
    ? `a céged „${props.costCenterName}" költséghelyéhez tartozik`
    : "a céged egy másik költséghelyéhez tartozik";
  return `Ez az oldal ${where}. Kérj hozzáférést a céged adminisztrátorától.`;
};

// No status code in the headline: the user is not looking for one. The trace id is the
// exception, and it sits below — it exists for support, not for the reader.
const content = computed(() => {
  switch (props.kind) {
    case "notFound":
      return {
        icon: "mdi-file-question-outline",
        title: "A keresett oldal nem található",
        body: "Lehet, hogy törölték, vagy elgépelted a linket.",
      };
    case "forbidden":
      return props.costCenter
        ? {
            icon: "mdi-lock-outline",
            title: "Nincs hozzáférésed ehhez a költséghelyhez",
            body: forbiddenBody(),
          }
        : {
            icon: "mdi-lock-outline",
            title: "Ehhez a művelethez nincs jogosultságod",
            body: "Ha úgy gondolod, hogy hozzá kellene férned, jelezd a céged adminisztrátorának.",
          };
    default:
      return {
        icon: "mdi-cloud-off-outline",
        title: "A szolgáltatás átmenetileg nem elérhető",
        body: "Próbáld újra egy perc múlva. Ha a hiba továbbra is fennáll, jelezd nekünk.",
      };
  }
});
</script>

<style scoped>
.cv-error-state {
  max-width: 520px;
  margin: 0 auto;
  padding: 64px 16px;
  text-align: center;
}

.cv-error-icon {
  color: var(--cv-text-faint);
}

.cv-error-body {
  color: var(--cv-text-muted);
}

.cv-error-trace {
  font-size: 0.8rem;
  color: var(--cv-text-faint);
}

.cv-error-trace code {
  font-family: monospace;
  background: var(--cv-background);
  padding: 2px 6px;
  border-radius: 4px;
}

.cv-error-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
}
</style>
